import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import {InputTextModule} from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

import { WorkerService } from '@layout/driver/services/worker.service';
import { Driver } from '@commons/models/domain/Driver';
import { WorkerModel } from '@shared/models/worker.model';
import { WorkerHttpService } from '@layout/driver/http-services/worker-http.service';
import { DriverLicesnseStatus } from '@src/app/enum/driver-license.enum';
import { driverLicenseNumberValidator } from '@src/app/utils/validators-forms/driver-license-number';
import { DriverLicenseHintComponent } from '../driver-license-hint/driver-license-hint.component';

@Component({
  selector: 'app-workers-add-form',
  standalone: true,
  imports: [CommonModule, InputTextModule, ButtonModule, DriverLicenseHintComponent, FormsModule, ReactiveFormsModule, TranslateModule, TooltipModule],
  templateUrl: './workers-add-form.component.html',
  styleUrls: ['./workers-add-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkersAddFormComponent implements OnInit {

  @Output() public onClickBack$: EventEmitter<void> = new EventEmitter();

  public form: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    phone: new FormControl(''),
    driverLicense: new FormControl('', [driverLicenseNumberValidator()]),
    email: new FormControl(''),
  });
  public isSaving: boolean;
  public isAddNewWorker: boolean = true;
  public editedWorkerData?: Driver;

  private get workerDataPassed(): { data: Driver } | null {
    try {
      const workerData = this.activeRoute.snapshot.params['workerData'];
      return workerData ? JSON.parse(workerData) : null;
    }
    catch {
      return null;
    }
  }

  constructor(
    private workerService: WorkerService,
    private workerHttpService: WorkerHttpService,
    private activeRoute: ActivatedRoute,
    private translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    this.editedWorkerData = this.workerDataPassed?.data;
    if (this.editedWorkerData) {
      this.initFormData();
      this.isAddNewWorker = false;
    }
  }

  public get driverLicesnseControl() {
    return this.form.get('driverLicense');
  }

  public archiveWorker(): void {
    this.workerService.archiveWorkers([this.editedWorkerData as Driver]);
    this.back();
  }

  public back(): void {
    this.onClickBack$.emit();
  }

  public submit(): void {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.isSaving = true;
      const worker = this.prepareDataWorker();
      const driver = this.workerToDriver(worker);
      this.isAddNewWorker ? this.addWorkerToDb(worker, driver) : this.updateWorkerToDb(worker, driver);
      return;
    }
    alert(this.translateService.instant('worker.error-form-add'));
  }

  private addWorkerToDb(worker: WorkerModel, driver: Driver): void {
    this.workerHttpService.add(worker).subscribe({
      next: (workerID) => {
        this.afterAddWorkerToDb(workerID, driver);
      },
      error: () => this.onError(),
    });
  }

  private updateWorkerToDb(worker: WorkerModel, driver: Driver): void {
    this.workerHttpService.update(worker).subscribe({
      next: () => {
        this.afterUpdateWorker(driver);
      },
      error: () => this.onError(),
    });
  }

  private onError(): void {
    alert(this.translateService.instant('errorTryAgain'));
    this.isSaving = false;
  }

  private afterUpdateWorker(driver: Driver): void {
    this.workerService.updateWorker(driver);
    this.isSaving = false;
    this.back();
  }

  private afterAddWorkerToDb(workerID: number, driver: Driver): void {
    driver = {
      ...driver,
      Id: workerID
    };
    this.workerService.addNewWorkerTolist(driver);
    this.isSaving = false;
    this.back();
  }

  private initFormData(): void {
    this.form.patchValue({
      firstName: this.editedWorkerData?.FN,
      lastName: this.editedWorkerData?.LN,
      phone: this.editedWorkerData?.CN,
      email: this.editedWorkerData?.Em,
      driverLicense: this.editedWorkerData?.L,
    });
  }

  private prepareDataWorker(): WorkerModel {
    const worker = {
      Id: this.editedWorkerData?.Id || 0,
      FirstName: this.form.value.firstName,
      LastName: this.form.value.lastName,
      PhoneNumber: this.form.value.phone,
      CellphoneNumber: this.form.value.phone,
      Email: this.form.value.email,
      FourDriverAccess: false,
      DriverLicenseNumber: this.form.value.driverLicense,
    } as WorkerModel;

    if (this.isAddNewWorker) {
      worker.OrganizationalUnitId = 1;
      worker.JobPositionId = 1;
      worker.AssignedVehicle = undefined;
    }
    return worker;
  }

  private workerToDriver(worker: WorkerModel): Driver {
    const driver = {} as Driver;
    driver.Id = worker.Id;
    driver.FN = worker.FirstName;
    driver.LN = worker.LastName;
    driver.Pn = worker.PhoneNumber;
    driver.CN = worker.CellphoneNumber;
    driver.Em = worker.Email ?? '';
    driver.L = worker.DriverLicenseNumber ?? '';
    driver.LS = DriverLicesnseStatus.NotCheckedYet;
    return driver;
  }

}
